import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { getArticles } from "@/services/cms/api";
import { ARTICLES_DEFAULT_POPULATE } from "@/components/blog/singleArticle/SingleArticle";
import SingleArticle from "@/components/blog/singleArticle";
import routes from "@/constants/routes";
import FlatsAndHousesToRent from "@/components/landing-new/FlatsAndHousesToRent";
import BeFirstInLineBanner from "@/components/properties/BeFirstInLineBanner";
import BeFirstThroughDoorBanner from "../BeFirstThroughDoorBanner/BeFirstThroughDoorBanner";
import BenefitsOfKeyzyNow from "@/components/landing-new/BenefitsOfKeyzyNow";
import GotQuestionsAboutNeighbourhood from "../GotQuestionsAboutNeighbourhood";

const MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#1d2c4d" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8ec3b9" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a3646" }] },
  {
    featureType: "administrative.country",
    elementType: "geometry.stroke",
    stylers: [{ color: "#4b6878" }],
  },
  {
    featureType: "administrative.land_parcel",
    elementType: "labels.text.fill",
    stylers: [{ color: "#64779e" }],
  },
  {
    featureType: "administrative.province",
    elementType: "geometry.stroke",
    stylers: [{ color: "#4b6878" }],
  },
  {
    featureType: "landscape.man_made",
    elementType: "geometry.stroke",
    stylers: [{ color: "#334e87" }],
  },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#023e58" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#283d6a" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#6f9ba5" }] },
  { featureType: "poi", elementType: "labels.text.stroke", stylers: [{ color: "#1d2c4d" }] },
  { featureType: "poi.park", elementType: "geometry.fill", stylers: [{ color: "#023e58" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#3C7680" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#304a7d" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#98a5be" }] },
  { featureType: "road", elementType: "labels.text.stroke", stylers: [{ color: "#1d2c4d" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2c6675" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#255763" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#b0d5ce" }] },
  {
    featureType: "road.highway",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#023e58" }],
  },
  { featureType: "transit", elementType: "labels.text.fill", stylers: [{ color: "#98a5be" }] },
  { featureType: "transit", elementType: "labels.text.stroke", stylers: [{ color: "#1d2c4d" }] },
  { featureType: "transit.line", elementType: "geometry.fill", stylers: [{ color: "#283d6a" }] },
  { featureType: "transit.station", elementType: "geometry", stylers: [{ color: "#3a4762" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e1626" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4e6d70" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit.station", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
];

const WANDSWORTH_CENTER = { lat: 51.43798, lng: -0.17443 };

type TransportType = "tube" | "rail" | "bus";

const TRANSPORT_STOPS: { lat: number; lng: number; name: string; type: TransportType }[] = [
  { lat: 51.4352, lng: -0.1548, name: "Tooting Bec", type: "tube" },
  { lat: 51.4432, lng: -0.1527, name: "Balham", type: "tube" },
  { lat: 51.4274, lng: -0.1894, name: "Earlsfield", type: "rail" },
  { lat: 51.4413, lng: -0.1714, name: "Wandsworth Common", type: "rail" },
  { lat: 51.4393, lng: -0.17343, name: "Springfield Hospital (Stop Z)", type: "bus" },
  { lat: 51.43883, lng: -0.17252, name: "Springfield University Hospital (Stop Y)", type: "bus" },
];

const TRANSPORT_ICONS: Record<TransportType, { path: string; w: number; h: number }> = {
  tube: { path: "/properties/tfl-underground-logo.svg", w: 45, h: 37 },
  rail: { path: "/properties/national-rail-logo.svg", w: 29, h: 29 },
  bus: { path: "/properties/tfl-bus-logo.svg", w: 45, h: 37 },
};

const WandsworthBrochure = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [blogArticles, setBlogArticles] = useState<any[]>([]);
  const [isGoogleMapsApiLoaded, setIsGoogleMapsApiLoaded] = useState(false);
  const [showTransportLinks, setShowTransportLinks] = useState(false);
  const mapInitialised = useRef(false);
  const transportMarkersRef = useRef<any[]>([]);
  const slides = [1, 2, 3, 4, 5, 6];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchBlogArticles = async () => {
      try {
        const response = await getArticles({
          filters: {
            id: { $in: [1203, 642] },
          },
          populate: ARTICLES_DEFAULT_POPULATE,
          pagination: { page: 1, pageSize: 2 },
        });
        setBlogArticles(response.data);
      } catch (error) {
        console.error("Error fetching blog articles:", error);
      }
    };
    fetchBlogArticles();
  }, []);

  const initMap = (): void => {
    if (typeof window === "undefined" || !window.google || !window.google.maps) return;
    if (mapInitialised.current) return;
    mapInitialised.current = true;

    const styledMapType = new window.google.maps.StyledMapType(MAP_STYLES, { name: "Styled Map" });
    const map = new window.google.maps.Map(
      document.getElementById("wandsworthAreaMap") as HTMLElement,
      {
        center: WANDSWORTH_CENTER,
        zoom: 14,
        disableDefaultUI: true,
        keyboardShortcuts: false,
        backgroundColor: "#1d2c4d",
        mapTypeControlOptions: {
          mapTypeIds: ["roadmap", "satellite", "hybrid", "terrain", "styled_map"],
        },
      },
    );
    map.mapTypes.set("styled_map", styledMapType);
    map.setMapTypeId("styled_map");

    const svgMarker = {
      path: "M23.9957 0C10.3207 0 0 10.9296 0 25.4159C0 43.6493 22.31 60.0654 23.256 60.7577C23.4797 60.9221 23.7377 61 23.9957 61C24.2537 61 24.5117 60.9221 24.7354 60.7577C25.6814 60.0654 47.9914 43.6493 47.9914 25.4159C48 10.9296 37.6793 0 23.9957 0ZM23.9957 38.1282C16.3326 38.1282 10.1057 31.8543 10.1057 24.1525C10.1057 16.4507 16.3412 10.1768 23.9957 10.1768C31.6502 10.1768 37.8857 16.4507 37.8857 24.1525C37.8857 31.8543 31.6502 38.1282 23.9957 38.1282Z",
      fillColor: "#F39237",
      fillOpacity: 1,
      strokeWeight: 0,
      rotation: 0,
      scale: 0.5,
      anchor: new window.google.maps.Point(27, 55),
    };

    new window.google.maps.Marker({
      position: WANDSWORTH_CENTER,
      icon: svgMarker,
      map,
    });

    transportMarkersRef.current = TRANSPORT_STOPS.map((stop) => {
      const { path, w, h } = TRANSPORT_ICONS[stop.type];
      const icon = {
        url: window.location.origin + path,
        scaledSize: new window.google.maps.Size(w, h),
        anchor: new window.google.maps.Point(w / 2, h / 2),
      };
      const marker = new window.google.maps.Marker({
        position: { lat: stop.lat, lng: stop.lng },
        icon,
        title: stop.name,
        map,
        visible: false,
      });
      const infoWindow = new window.google.maps.InfoWindow({ content: stop.name });
      marker.addListener("click", () => infoWindow.open(map, marker));
      return marker;
    });
  };

  const loadGoogleMapsApi = (): void => {
    const existingScript = document.getElementById("google-maps-api") as HTMLScriptElement | null;
    const onReady = () => {
      if (window.google && window.google.maps) setIsGoogleMapsApiLoaded(true);
    };
    if (existingScript) {
      if (window.google && window.google.maps) onReady();
      else existingScript.addEventListener("load", onReady, { once: true });
      return;
    }
    const apiKey =
      process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "AIzaSyCZ3c0k0tGh2mErBmcm_TyXrQAEEiD5Gjw";
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&language=en&v=weekly`;
    script.id = "google-maps-api";
    script.async = false;
    script.defer = true;
    script.onload = onReady;
    document.body.appendChild(script);
  };

  useEffect(() => {
    if (!isGoogleMapsApiLoaded) {
      loadGoogleMapsApi();
    } else {
      initMap();
    }
  }, [isGoogleMapsApiLoaded]);

  useEffect(() => {
    transportMarkersRef.current.forEach((marker) => marker.setVisible(showTransportLinks));
  }, [showTransportLinks]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="w-full">
      {/* Full-width Carousel */}
      <div className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={slide}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <Image
              src={`/properties/wandsworth/${slide}.jpg`}
              alt={`Wandsworth ${slide}`}
              layout="fill"
              objectFit="cover"
              priority={index === 0}
            />
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
          aria-label="Previous slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
          aria-label="Next slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentSlide ? "bg-white" : "bg-white/50"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      <div className="py-12">
        <BeFirstInLineBanner />
      </div>

      {/* Section 1 */}
      <div className="flex flex-col md:flex-row items-center gap-8 px-4 py-12 md:px-8 lg:px-16 max-w-7xl mx-auto">
        <div className="w-full md:w-1/2 relative h-[300px] md:h-[400px]">
          <Image
            src="/properties/wandsworth/area1.jpg"
            alt="Wandsworth Area 1"
            layout="fill"
            objectFit="cover"
            className="rounded-lg"
          />
        </div>
        <div className="w-full md:w-1/2">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900">
            Discover Wandsworth’s Charm
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Wandsworth offers a perfect blend of urban sophistication and green spaces. With
            excellent transport links, vibrant local amenities, and a strong sense of community,
            it’s an ideal location for those seeking a balanced lifestyle in South West London.
          </p>
        </div>
      </div>

      {/* Section 2 */}
      <div className="flex flex-col md:flex-row-reverse items-center gap-8 px-4 py-12 md:px-8 lg:px-16 max-w-7xl mx-auto bg-gray-50">
        <div className="w-full md:w-1/2 relative h-[300px] md:h-[400px]">
          <Image
            src="/properties/wandsworth/area2.jpg"
            alt="Wandsworth Area 2"
            layout="fill"
            objectFit="cover"
            className="rounded-lg"
          />
        </div>
        <div className="w-full md:w-1/2">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900">
            Rent-to-Own Made Simple
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Our Rent-to-Own program in Wandsworth gives you the flexibility to rent your dream home
            today with the option to buy it in the future. Build equity while you rent, with no
            obligation to purchase. Take control of your homeownership journey on your terms.
          </p>
        </div>
      </div>

      {/* Section 3 */}
      <div className="flex flex-col md:flex-row items-center gap-8 px-4 py-12 md:px-8 lg:px-16 max-w-7xl mx-auto">
        <div className="w-full md:w-1/2 relative h-[300px] md:h-[400px]">
          <Image
            src="/properties/wandsworth/area3.jpg"
            alt="Wandsworth Area 3"
            layout="fill"
            objectFit="cover"
            className="rounded-lg"
          />
        </div>
        <div className="w-full md:w-1/2">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900">
            Your Future Starts Here
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Join the growing community of homeowners who have discovered the Keyzy advantage. With
            transparent pricing, flexible terms, and dedicated support, we make the path to
            homeownership accessible and stress-free. Start your journey in Wandsworth today.
          </p>
        </div>
      </div>

      {/* Map */}
      <div className="relative w-full" style={{ height: "480px" }}>
        <div id="wandsworthAreaMap" className="absolute inset-0" />
        <button
          onClick={() => setShowTransportLinks((prev) => !prev)}
          className={`absolute top-3 right-3 z-10 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold shadow-md transition-colors ${
            showTransportLinks ? "bg-landing-blue text-white" : "bg-white text-landing-blue"
          }`}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/properties/tfl-bus-logo.svg" alt="" width={24} height={20} />
          <span className="hidden md:inline">Transport Links</span>
        </button>
      </div>

      {/* Blog Posts */}
      {blogArticles.length > 0 && (
        <div className="flex flex-col px-5 lg:px-[3rem] items-center justify-center">
          <div className="relative bg-white w-full overflow-hidden items-center">
            <div className="flex left-0 right-0 overflow-hidden bg-center bg-no-repeat bg-contain h-24 md:h-40 bg-[url('/intermediaries/city-article-ribbon-desktop.svg')]" />
          </div>

          <h2 className="text-4xl font-bold text-center xl:text-5xl text-landing-blue my-0 lg:my-4 xl:mt-8">
            Related articles
          </h2>
          <section className="flex flex-wrap items-center justify-between w-auto mt-5 max-w-[90rem] md:mb-12 mx-auto xl:px-[3.75rem] lg:w-3/4">
            {blogArticles.map((article, idx) => (
              <div key={idx} className="flex flex-col w-full md:w-1/2 md:px-4 py-4 md:py-0">
                <SingleArticle article={article as any} baseUrl={routes.blog} />
              </div>
            ))}
          </section>

          <div className="absolute hidden transform rotate-180 -left-10 -top-5 w-96 h-36 xl:inline-block">
            <Image
              src="/shared/shapes.svg"
              alt="shapes doodles"
              objectFit="contain"
              layout="fill"
              priority
            />
          </div>

          <GotQuestionsAboutNeighbourhood neighbourhood="wandsworth" />
        </div>
      )}
    </div>
  );
};

export default WandsworthBrochure;
